import styled from '@emotion/styled'
import React, { FC, KeyboardEvent, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 as uuid } from 'uuid'

import { useIsMobile } from '../../hooks/useBreakpoint'
import { ColumnFlex, VisuallyHidden } from '../Layout/Layout'

import SearchEndAdornment from './SearchEndAdornment'
import StyledTextField from './SearchStyledTextField'
import TextFieldHeading from './SearchTextFieldHeading'

const InputContainer = styled(ColumnFlex)`
  padding: 0 16px;
`

// eslint-disable-next-line @typescript-eslint/ban-types
interface Props {
  label: string
  placeholder?: string
  searchWord: string
  setSearchWord: (value: string) => void
  autoFocus?: boolean
  moveAutoFocusToResults?: (value: boolean) => void
}

const SearchAutocomplete: FC<Props> = ({
  label,
  placeholder,
  searchWord,
  setSearchWord,
  autoFocus,
  moveAutoFocusToResults,
}): JSX.Element => {
  const { t } = useTranslation()
  const isMobile = useIsMobile()

  const textFieldId = useMemo(() => `searchTextField-${uuid()}`, [])

  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (autoFocus && document.activeElement !== inputRef.current) {
      inputRef.current?.focus()
    }
  })

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      moveAutoFocusToResults && moveAutoFocusToResults(true)
    }
  }

  const onAdormentClick = (value: string) => {
    setSearchWord(value)
    inputRef.current?.focus()
  }

  const onFocus = () => {
    moveAutoFocusToResults && moveAutoFocusToResults(false)
  }

  const headingId = 'search-autocomplete-heading-id'
  return (
    <>
      <InputContainer>
        <TextFieldHeading $size={isMobile ? 300 : 400}>{label}</TextFieldHeading>
        <StyledTextField
          aria-labelledby={headingId}
          value={searchWord}
          inputRef={inputRef}
          onChange={(event) => setSearchWord(event.target.value)}
          id={textFieldId}
          size="medium"
          variant="outlined"
          placeholder={placeholder ?? t('component.search.searchTextField.placeholder')}
          slotProps={{
            input: {
              endAdornment: <SearchEndAdornment value={searchWord} setValue={onAdormentClick} />,
              onKeyDown: onKeyDown,
            },
          }}
          onFocus={onFocus}
          aria-label={label}
          aria-describedby={`${textFieldId}-info`}
        />
      </InputContainer>
      <VisuallyHidden id={`${textFieldId}-info`}>
        {t('component.search.searchTextField.ariaDescription')}
      </VisuallyHidden>
    </>
  )
}

export default SearchAutocomplete
